import axios from 'axios';
import { toast } from 'react-toastify';
import { getAdminApiBaseUrl } from './adminApiBase';

const ADMIN_API_BASE = getAdminApiBaseUrl();

function isUsableStoredAdminToken(token) {
    if (token == null || typeof token !== 'string') return false;
    const s = token.trim();
    return Boolean(s) && s !== 'null' && s !== 'undefined';
}

// axios 인스턴스 생성
const api = axios.create({
    baseURL: ADMIN_API_BASE,
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
    },
});

// 요청 인터셉터 설정
api.interceptors.request.use(
    config => {
        const token = localStorage.getItem('admin-token');
        if (isUsableStoredAdminToken(token)) {
            config.headers['Authorization'] = `Bearer ${token}`;
            console.log('토큰이 헤더에 추가됨:', token.substring(0, 15) + '...');
        } else {
            delete config.headers['Authorization'];
            console.log('인증 토큰이 없음');
        }
        return config;
    },
    error => {
        console.error('API 요청 인터셉터 에러:', error);
        return Promise.reject(error);
    }
);

// 토큰 갱신 함수
const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem('admin-refresh-token');
    if (!refreshToken) {
        throw new Error('리프레시 토큰이 없습니다.');
    }

    // 세션 ID 확인
    const sessionId = localStorage.getItem('admin-session-id');
    if (!sessionId) {
        throw new Error('세션 ID가 없습니다.');
    }

    console.log('리프레시 토큰으로 새 토큰 요청 시도...');

    const response = await axios.post(`${ADMIN_API_BASE}/auth/refresh-token`, {
        refresh_token: refreshToken,
        session_id: sessionId,
    });

    if (response.data.token) {
        console.log('새 토큰 발급 성공');
        localStorage.setItem('admin-token', response.data.token);

        if (response.data.refreshToken) {
            localStorage.setItem('admin-refresh-token', response.data.refreshToken);
        }

        // 새 세션 ID가 있다면 저장
        if (response.data.sessionId) {
            localStorage.setItem('admin-session-id', response.data.sessionId);
        }

        // 새 토큰으로 헤더 업데이트
        api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
        return response.data.token;
    }

    throw new Error('토큰 갱신에 실패했습니다.');
};

function clearAdminAuthStorage() {
    localStorage.removeItem('admin-token');
    localStorage.removeItem('admin-user');
    localStorage.removeItem('admin-refresh-token');
    localStorage.removeItem('admin-session-id');
    delete api.defaults.headers.common['Authorization'];
}

function redirectToLoginIfNeeded() {
    if (/\/login\/?$/i.test(window.location.pathname || '')) {
        return;
    }
    window.location.replace('/login');
}

// 응답 인터셉터 설정
api.interceptors.response.use(
    response => {
        return response;
    },
    async error => {
        const originalRequest = error.config;

        // 401 Unauthorized - 토큰 만료 또는 인증 실패
        if (error.response?.status === 401 && !originalRequest._retry) {
            console.log('401 인증 오류 발생: 토큰 만료 또는 인증 실패');
            originalRequest._retry = true;

            try {
                // 새 토큰 요청
                const newToken = await refreshAccessToken();

                // 원래 요청 재시도
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                return api(originalRequest);
            } catch (refreshError) {
                console.error('토큰 갱신 실패:', refreshError);
                clearAdminAuthStorage();
                toast.error('세션이 만료되었습니다. 다시 로그인해주세요.');
                redirectToLoginIfNeeded();
                return Promise.reject(refreshError);
            }
        }

        // 403 Forbidden - 권한 없음
        if (error.response?.status === 403) {
            console.error('접근 권한 없음 (403 Forbidden):', error.response.data);

            // 사용자에게 알림 표시
            toast.error('권한이 없거나 세션이 만료되었습니다. 다시 로그인해주세요.');

            clearAdminAuthStorage();
            redirectToLoginIfNeeded();
        }

        return Promise.reject(error);
    }
);

export default api;
